import * as THREE from "three";
import { getWorldYRotation } from "../../utils/geometry.js";
import { isFreeStanding } from "../../utils/constants.js";
import { _Y_AXIS } from "./shared.js";

const ROTATION_ANGLE = Math.PI / 2; // 90° increment — snapped modules only meet at right angles
const ROTATION_ANGLE_FREE = Math.PI / 4; // 45° increment when snapping is off and any angle is placeable

export const rotationMethods = {
  // Rotation increment for R / the rotate button. With snapping on, modules only ever
  // mate at right angles, so anything finer than 90° just needs undoing. With snapping
  // off nothing constrains the angle, so allow finer 45° steps.
  get rotationStep() {
    return this.snappingEnabled ? ROTATION_ANGLE : ROTATION_ANGLE_FREE;
  },

  // Rotate a module by angleY (typically 90°) and re-snap it to the closest compatible AP at the
  // new angle, preserving the user's chosen orientation; left rotated and free if no valid snap.
  rotateModuleAndResnap(furniture, angleY) {
    if (!furniture || !furniture.getMesh()) return;
    if (!isFreeStanding(furniture)) return;

    // An accessory has no module group and no AP to re-bind, so it just turns about its own origin.
    if (furniture.furnitureData.componentType !== 'module') {
      furniture.rotate(angleY);
      const resolved = this.resolveModuleCollision(furniture, furniture.getMesh().position.clone());
      furniture.setPosition(resolved.x, resolved.y, resolved.z);
      this.app.getSceneManager()?.requestShadowUpdate();
      return;
    }

    // Bail on mid-chain/fork modules: re-snap can only re-bind one neighbour, orphaning the rest.
    const moduleChildren = furniture.attachedChildren.filter(
      c => c.furnitureData?.componentType === 'module'
    );
    const moduleNeighbors = (furniture.attachedTo ? 1 : 0) + moduleChildren.length;
    if (moduleNeighbors > 1) return;

    const scene = this.app.getScene();
    const mesh = furniture.getMesh();

    // Everything needed to put the piece back untouched if no orientation fits.
    const original = {
      position: mesh.position.clone(),
      quaternion: mesh.quaternion.clone(),
      attachedTo: furniture.attachedTo,
      connectionSide: furniture.moduleConnectionSide ?? null,
      children: moduleChildren.map(c => ({ child: c, ap: c.attachedTo, side: c.moduleConnectionSide ?? null })),
    };

    // Detach module neighbours (not scene-graph children) so their freed APs become re-snap candidates.
    for (const child of moduleChildren) {
      child.detachFrom(scene);
    }
    if (furniture.attachedTo) {
      furniture.detachFrom(scene);
    }

    // An attached piece re-attaches to the group it just left; a free one to whatever is in snap reach.
    const formerNeighbours = [original.attachedTo?.parentFurniture, ...moduleChildren].filter(Boolean);
    const parents = new Set();
    for (const n of formerNeighbours) for (const m of n.getAllGroupModules()) parents.add(m);

    // Capture the true world center before rotating so a free (un-snapped) piece can be
    // restored in place rather than pivoted about its origin. Recompute first so the
    // center reflects any pose change since the last full box (variant or parent moves).
    furniture.computeBoundingBoxFull();
    const centerBefore = furniture.boundingBox
      ? new THREE.Vector3().copy(furniture.boundingBox.center)
      : null;

    this.app.getSceneManager()?.requestShadowUpdate();

    // An attached piece walks the turn in steps until one orientation fits a free AP of its
    // group, so pressing rotate always lands it on the next valid face. If none does, it goes
    // back exactly as it was: turning in place would leave it inside its neighbours.
    const steps = parents.size > 0 ? Math.max(1, Math.round((2 * Math.PI) / Math.abs(angleY)) - 1) : 1;
    for (let k = 1; k <= steps; k++) {
      // rotate() applies mesh.rotateY AND recomputes the box. The re-snap search must run
      // against the natural post-rotateY origin, so don't pre-shift the mesh.
      furniture.rotate(angleY);
      if (this._resnapRotatedModule(furniture, parents.size > 0 ? parents : null)) {
        this.app.getFurnitureManager()?._debugRefreshModuleAPs?.();
        return;
      }
    }

    if (parents.size > 0) {
      this._restoreRotatedModule(furniture, original);
    } else if (centerBefore) {
      furniture.setCenterXZ(centerBefore.x, centerBefore.z);
    }
    this.app.getFurnitureManager()?._debugRefreshModuleAPs?.();
  },

  // Put a piece back at its pre-rotation pose and re-bind every link rotateModuleAndResnap cut.
  _restoreRotatedModule(furniture, original) {
    const mesh = furniture.getMesh();
    mesh.position.copy(original.position);
    mesh.quaternion.copy(original.quaternion);
    furniture.computeBoundingBoxFull();
    if (original.attachedTo) furniture.attachTo(original.attachedTo, original.connectionSide);
    for (const { child, ap, side } of original.children) {
      if (ap) child.attachTo(ap, side);
    }
  },

  // Serialize a module's attachment points into the plain shape the snap system expects.
  _serializeModuleAPs(furniture) {
    return furniture.attachmentPoints.map(ap => ({
      side: ap.side,
      offset: { x: ap.localPosition.x, y: ap.localPosition.y, z: ap.localPosition.z },
      accepts: ap.acceptsComponentTypes,
      anchorType: ap.anchorType ?? null,
      compatibilityTag: ap.compatibilityTag ?? 0,
      facingAngle: ap.facingAngle ?? null,
    }));
  },

  /**
   * Re-snap a just-rotated module at its new angle; returns true if attached. With `parents`
   * (the group it was detached from) every free face of that group is tried, nearest first,
   * keeping only snaps at the new angle, and the first that passes the snap gate wins. Without
   * them it takes the nearest snap in drag reach, but only when that snap keeps the new angle.
   */
  _resnapRotatedModule(furniture, parents = null) {
    const mesh = furniture.getMesh();
    mesh.updateMatrixWorld(true);
    const worldPos = mesh.getWorldPosition(new THREE.Vector3());
    const newRotation = getWorldYRotation(mesh);
    const draggedAPs = this._serializeModuleAPs(furniture);

    let candidates;
    if (parents) {
      candidates = this.snapSystem.findSnapsAtRotation(parents, furniture, draggedAPs, newRotation, worldPos);
    } else {
      const specialCase = furniture.furnitureData?.properties?.specialCaseSnapping === true;
      const candidate = this.findClosestModuleAttachment(worldPos, furniture, draggedAPs, newRotation, specialCase);
      const snap = candidate && this.calculateSnapPosition(candidate, draggedAPs, newRotation, worldPos, newRotation);
      candidates = snap ? [{ attachmentPoint: candidate, snap }] : [];
    }

    const fit = candidates.find(({ attachmentPoint, snap }) => this._rotatedSnapFits(furniture, attachmentPoint, snap));
    if (!fit) return false;
    const { attachmentPoint: candidate, snap } = fit;

    furniture.setPosition(snap.position.x, snap.position.y, snap.position.z);
    mesh.quaternion.setFromAxisAngle(_Y_AXIS, snap.rotation);
    furniture.attachTo(candidate, snap.childAttachmentSide || null);
    furniture.calculateBoundingBox();
    return true;
  },

  // The snap gate (bounds, collision, inside-placement) for a rotated module at `snap`.
  _rotatedSnapFits(furniture, candidate, snap) {
    // Materialise the group once for both the exclude set and the sibling-penetration scan.
    const parentGroup = [...candidate.parentFurniture.getAllGroupModules()];
    const exclude = new Set([...parentGroup, furniture]);
    return this.isSnapPositionInBounds(furniture, snap.position) &&
      !this.checkFurnitureCollisions(furniture, snap.position, exclude, snap.rotation) &&
      !this._snapPenetratesGroup(furniture, snap, candidate, parentGroup);
  },

  _rotateSelectionOrGhost(selectedFurnitureArray, selectedFurniture, furnitureManager) {
    if (selectedFurnitureArray.length > 1) {
      // Group rotation when select-all is active
      const allModules = this.app.getUIManager()?.selectAllTargets() ?? [];
      const isSelectAll = allModules.length > 0 && selectedFurnitureArray.length === allModules.length;
      if (isSelectAll) {
        this.app.getUIManager()?.rotateGroupAroundCenter(selectedFurnitureArray, this.rotationStep);
        furnitureManager?.updateSelectionOutline();
        this.app.notifyStateChanged();
      }
    } else if (selectedFurniture && selectedFurnitureArray.length === 1) {
      // Modules and floor-standing accessories turn; anything on an AP ignores R.
      if (!isFreeStanding(selectedFurniture)) return;
      this.rotateModuleAndResnap(selectedFurniture, this.rotationStep);
      furnitureManager?.updateSelectionOutline();
      this.app.notifyStateChanged();
    } else if (selectedFurnitureArray.length === 0) {
      // Rotate ghost furniture if in placement mode
      const ghostFurniture = furnitureManager?.getGhostFurniture();
      if (ghostFurniture && ghostFurniture.isVisible) {
        // Advance the stable selection rotation (the intended orientation), not the
        // possibly auto-rotated live rotation, so R steps are consistent and the
        // snap search sees the user's choice. Keep the two in sync.
        const base = this._ghostRotRef === ghostFurniture && this.ghostSelectRotation != null
          ? this.ghostSelectRotation
          : ghostFurniture.getCurrentRotation();
        const next = base + this.rotationStep;
        this._ghostRotRef = ghostFurniture;
        this.ghostSelectRotation = next;
        furnitureManager.updateGhostFurnitureRotation(next);
      }
    }
  },
};
